/**
 * sketchToSlideRenderer — Office.js layer that draws a `SlideSpec` (returned
 * by DeckorateAPI's POST /image/sketch-to-slide) as native, editable
 * PowerPoint shapes.
 *
 * The result lands on a NEW slide appended to the end of the deck — never on
 * top of the sketch's original slide — so a low-quality read of the sketch
 * never destroys or obscures the user's existing content; the sketch image
 * itself is left exactly where it was. (PowerPoint's JS API has no "insert
 * slide after index N" primitive — `slides.add()` only appends — so this
 * follows the same append-then-leave-in-place approach `orgChartBuilder.ts`'s
 * `addNewSlide` already uses in this codebase.)
 *
 * Geometry in `SlideSpec` is percentage-of-slide, top-left-corner + size
 * (x_pct/y_pct/width_pct/height_pct), independent of the sketch photo's own
 * aspect ratio — it is converted here to points against the PRESENTATION's
 * actual slide size, then clamped on-slide in case the model's numbers push a
 * shape out of bounds.
 *
 * `style.fill_color_token`/`line_color_token` name a slot in the deck's theme
 * (e.g. "accent1"). The current sketch-to-slide prompt always returns these as
 * null (DeckorateAPI/prompts/sketch_to_slide_api/sketch_to_slide.py says so
 * explicitly), so today every shape falls through to the role-based defaults
 * below — but the resolution path is implemented for when that changes.
 */

import { logError, scrubMessage } from "../../../../../telemetry";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { readSlideSizePts as readSlideSizePtsShared } from "../../shared/services/slideImageCapture";
import type { ThemeColors } from "../../create-slide/service/slideGenerationApi";
import type { SlideConnector, SlideConnectorStyle, SlideShape, SlideSpec } from "./sketchToSlideApi";

const RENDER_CONNECTOR = "deckorate";

export const ERR_NO_PPT = "Sketch to Slide requires running inside PowerPoint.";
export const ERR_EMPTY_SPEC = "The sketch produced no recognisable shapes to draw.";

/** Shape-name prefixes so a rendered slide can be identified/regrouped later. */
const SHAPE_PREFIX = "Sketch_Shape_";
const TEXT_PREFIX = "Sketch_Text_";
const CONNECTOR_PREFIX = "Sketch_Conn_";
const CONNECTOR_LABEL_PREFIX = "Sketch_ConnLabel_";
const TITLE_PREFIX = "Sketch_Title_";

/** Minimum on-slide dimension (points) so a near-zero AI estimate still renders visibly. */
const MIN_SHAPE_DIM_PTS = 6;
/** Gap left between a connector's endpoint and the shape edge it leaves/enters. */
const CONNECTOR_GAP_PTS = 3;
const CONNECTOR_LINE_COLOR = "#ABABAB";
const CONNECTOR_LINE_WEIGHT = 1.5;
const CONNECTOR_LABEL_FONT_SIZE = 9;

const DEFAULT_CONTAINER_FILL = "0078D4";
const DEFAULT_TEXT_COLOR = "323130";
const DEFAULT_LABEL_TEXT_COLOR = "605E5C";
const IMAGE_PLACEHOLDER_FILL = "F3F2F1";
const IMAGE_PLACEHOLDER_LINE = "A0A0A0";
const WHITE = "FFFFFF";

const TITLE_FONT_SIZE = 20;
const BODY_FONT_SIZE = 12;
const LABEL_FONT_SIZE = 10;
const CONTAINER_FONT_SIZE = 12;
const IMAGE_CAPTION_FONT_SIZE = 10;
const IMAGE_PLACEHOLDER_CAPTION = "Image placeholder";

function toTelemetryError(error: unknown): { type: string; scrubbedMessage: string } {
  return {
    type: error instanceof Error ? error.name : "SketchToSlideRenderError",
    scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
  };
}

function logRenderFailure(reason: string, error: unknown): void {
  logError("error.connector.failure", toTelemetryError(error), { connectorSystemName: RENDER_CONNECTOR, reason });
}

/** Prefixes a caught error's message with the shape/connector id it came from,
 *  so a per-item telemetry log stays attributable when several items fail in
 *  the same run instead of only ever surfacing the last one. */
function withContext(contextId: string, error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error);
  return new Error(`[${contextId}] ${message}`);
}

/** Adapts logRenderFailure's `(reason, error)` shape to shared readSlideSizePts's
 *  `(reason, message)` logger — wrapping the message back into an Error keeps
 *  the logged telemetry shape (type/scrubbedMessage) identical to a direct catch. */
function logRenderFailureMessage(reason: string, message: string): void {
  logRenderFailure(reason, new Error(message));
}

/** Read the presentation's actual slide size in points, falling back to the
 *  standard 16:9 default if pageSetup is unreadable. */
function readSlideSizePts(context: PowerPoint.RequestContext): Promise<{ widthPts: number; heightPts: number }> {
  return readSlideSizePtsShared(context, logRenderFailureMessage, "sketch-to-slide-render-slide-size-read");
}

/** Add a slide to the end of the deck and return it with its shapes ready to draw on.
 *  Nothing to clean up on failure here — no slide has been added, or the add
 *  itself is what failed — so this only logs and rethrows. */
async function addNewSlide(context: PowerPoint.RequestContext): Promise<PowerPoint.Slide> {
  try {
    const presentation = context.presentation;
    presentation.slides.add();
    await context.sync();
    const count = presentation.slides.getCount();
    await context.sync();
    const slide = presentation.slides.getItemAt(count.value - 1);
    slide.load("id");
    await context.sync();
    return slide;
  } catch (error) {
    logRenderFailure("sketch-to-slide-render-add-slide", error);
    throw error;
  }
}

/** Best-effort removal of a partially-drawn slide once something downstream has
 *  already failed — the user should never be left with a half-built, broken
 *  result on a stray new slide. Failure to delete is logged but never masks
 *  (or replaces) the original error that triggered the cleanup. */
async function cleanupFailedSlide(context: PowerPoint.RequestContext, slide: PowerPoint.Slide, cause: unknown): Promise<void> {
  logRenderFailure("sketch-to-slide-render-failed", cause);
  try {
    slide.delete();
    await context.sync();
  } catch (cleanupError) {
    logRenderFailure("sketch-to-slide-render-cleanup-failed", cleanupError);
  }
}

/* ── Geometry ────────────────────────────────────────────────────────────── */

interface RectPts {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly cx: number;
  readonly cy: number;
}

function toRectPts(shape: SlideShape, slideWidthPts: number, slideHeightPts: number): RectPts {
  const width = Math.min(slideWidthPts, Math.max(MIN_SHAPE_DIM_PTS, (shape.geometry.widthPct / 100) * slideWidthPts));
  const height = Math.min(slideHeightPts, Math.max(MIN_SHAPE_DIM_PTS, (shape.geometry.heightPct / 100) * slideHeightPts));
  const rawLeft = (shape.geometry.xPct / 100) * slideWidthPts;
  const rawTop = (shape.geometry.yPct / 100) * slideHeightPts;
  const left = Math.min(Math.max(0, rawLeft), slideWidthPts - width);
  const top = Math.min(Math.max(0, rawTop), slideHeightPts - height);
  return { left, top, width, height, cx: left + width / 2, cy: top + height / 2 };
}

/* ── shape_type (OOXML prstGeom hint) -> Office.js GeometricShapeType ───────
 * Deliberately plain string literals, not `PowerPoint.GeometricShapeType.*`
 * member access: this table is built at module load time, before `hasPpt()`
 * has run, and the `PowerPoint` global may not exist yet (or ever, in the
 * Word/Outlook hosts this add-in also ships to) — touching it eagerly here
 * would throw on import instead of failing gracefully inside renderSlideSpec.
 * `addGeometricShape`'s string-literal overload accepts these directly. */
type GeometricShapePreset = Parameters<PowerPoint.ShapeCollection["addGeometricShape"]>[0];

const GEOMETRIC_SHAPE_TYPE_BY_PRST: Record<string, GeometricShapePreset> = {
  rect: "Rectangle",
  rectangle: "Rectangle",
  roundRect: "RoundRectangle",
  ellipse: "Ellipse",
  oval: "Ellipse",
  diamond: "Diamond",
  triangle: "Triangle",
  rtTriangle: "RightTriangle",
  parallelogram: "Parallelogram",
  trapezoid: "Trapezoid",
  pentagon: "Pentagon",
  hexagon: "Hexagon",
  heptagon: "Heptagon",
  octagon: "Octagon",
  cloud: "Cloud",
  can: "Can",
  cylinder: "Can",
  chevron: "Chevron",
  star5: "Star5",
  rightArrow: "RightArrow",
  leftArrow: "LeftArrow",
  upArrow: "UpArrow",
  downArrow: "DownArrow",
  flowChartProcess: "FlowChartProcess",
  flowChartAlternateProcess: "FlowChartAlternateProcess",
  flowChartDecision: "FlowChartDecision",
  flowChartInputOutput: "FlowChartInputOutput",
  flowChartPredefinedProcess: "FlowChartPredefinedProcess",
  flowChartInternalStorage: "FlowChartInternalStorage",
  flowChartDocument: "FlowChartDocument",
  flowChartMultidocument: "FlowChartMultidocument",
  flowChartTerminator: "FlowChartTerminator",
  flowChartPreparation: "FlowChartPreparation",
  flowChartManualInput: "FlowChartManualInput",
  flowChartManualOperation: "FlowChartManualOperation",
  flowChartConnector: "FlowChartConnector",
  flowChartOffpageConnector: "FlowChartOffpageConnector",
  flowChartOr: "FlowChartOr",
  flowChartSummingJunction: "FlowChartSummingJunction",
  flowChartDelay: "FlowChartDelay",
  flowChartDisplay: "FlowChartDisplay",
  flowChartOnlineStorage: "FlowChartOnlineStorage",
  flowChartOfflineStorage: "FlowChartOfflineStorage",
};

function resolveGeometricShapeType(shapeType: string | null): GeometricShapePreset | null {
  if (!shapeType) return null;
  return GEOMETRIC_SHAPE_TYPE_BY_PRST[shapeType] ?? null;
}

/* ── Theme token resolution ──────────────────────────────────────────────── */

const THEME_COLOR_KEYS = new Set<keyof ThemeColors>([
  "dk1", "lt1", "dk2", "lt2", "accent1", "accent2", "accent3", "accent4", "accent5", "accent6", "hlink",
]);

function normalizeHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const clean = value.replace(/^#/, "").trim();
  return /^[0-9a-fA-F]{6}$/.test(clean) ? clean.toUpperCase() : null;
}

/** Resolve a style token (e.g. "accent1") against the deck's real theme colours.
 *  Returns null when the token is absent, unrecognised, or the theme has no
 *  value for it — callers fall back to a role-based default in that case. */
function resolveToken(token: string | null, themeColors: ThemeColors | null | undefined): string | null {
  if (!token || !themeColors) return null;
  if (!THEME_COLOR_KEYS.has(token as keyof ThemeColors)) return null;
  return normalizeHex(themeColors[token as keyof ThemeColors]);
}

/* ── Shape drawing ───────────────────────────────────────────────────────── */

interface StyledText {
  readonly box: PowerPoint.Shape;
  readonly size: number;
  readonly bold: boolean;
  readonly color: string; // hex, no leading "#"
  readonly align: PowerPoint.ParagraphHorizontalAlignment;
  readonly italic?: boolean;
  /** Source shape/connector id, purely for attributing a per-item styling
   *  failure to something diagnosable in telemetry. */
  readonly contextId: string;
}

function fontSizeFor(shape: SlideShape): number {
  if (shape.style.fontToken === "heading") return TITLE_FONT_SIZE;
  if (shape.style.fontToken === "body") return BODY_FONT_SIZE;
  switch (shape.role) {
    case "title":
      return TITLE_FONT_SIZE;
    case "label":
      return LABEL_FONT_SIZE;
    case "container":
      return CONTAINER_FONT_SIZE;
    default:
      return BODY_FONT_SIZE;
  }
}

/** A shape with no `shape_type` and a text-only role (title/body/label) draws
 *  as a plain text box with no background — the sketch's box around it, if
 *  any, wasn't confidently recognised as a distinct geometric shape. */
function drawTextOnlyShape(
  slide: PowerPoint.Slide,
  shape: SlideShape,
  rect: RectPts,
  themeColors: ThemeColors | null | undefined,
  texts: StyledText[],
): PowerPoint.Shape {
  const box = slide.shapes.addTextBox(shape.text ?? "", {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  });
  box.name = TEXT_PREFIX + shape.id;

  const color =
    resolveToken(shape.style.lineColorToken, themeColors) ??
    (shape.role === "label" ? DEFAULT_LABEL_TEXT_COLOR : DEFAULT_TEXT_COLOR);
  texts.push({
    box,
    size: fontSizeFor(shape),
    bold: shape.role === "title",
    color,
    align: shape.role === "title" ? PowerPoint.ParagraphHorizontalAlignment.center : PowerPoint.ParagraphHorizontalAlignment.left,
    contextId: shape.id,
  });
  return box;
}

/** A container (or any shape with a recognised `shape_type`) draws as a real
 *  geometric shape, with its text — if any — centered inside. */
function drawContainerShape(
  slide: PowerPoint.Slide,
  shape: SlideShape,
  rect: RectPts,
  geometricType: GeometricShapePreset,
  themeColors: ThemeColors | null | undefined,
  texts: StyledText[],
): PowerPoint.Shape {
  const fill = resolveToken(shape.style.fillColorToken, themeColors) ?? DEFAULT_CONTAINER_FILL;
  const line = resolveToken(shape.style.lineColorToken, themeColors) ?? fill;

  const box = slide.shapes.addGeometricShape(geometricType, {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  });
  box.name = SHAPE_PREFIX + shape.id;
  box.fill.setSolidColor(fill);
  box.lineFormat.color = line;
  box.lineFormat.weight = 1.5;

  if (shape.text) {
    const isDiamond = geometricType === "Diamond";
    const padX = isDiamond ? rect.width * 0.2 : 6;
    const padY = isDiamond ? rect.height * 0.2 : 4;
    const label = slide.shapes.addTextBox(shape.text, {
      left: rect.left + padX,
      top: rect.top + padY,
      width: Math.max(1, rect.width - padX * 2),
      height: Math.max(1, rect.height - padY * 2),
    });
    label.name = TEXT_PREFIX + shape.id;
    texts.push({
      box: label,
      size: fontSizeFor(shape),
      bold: true,
      color: WHITE,
      align: PowerPoint.ParagraphHorizontalAlignment.center,
      contextId: shape.id,
    });
  }

  return box;
}

/** `role: "image"` can never be a real photo — the backend only reports
 *  structure, not pixels — so it always renders as an honest placeholder
 *  rather than silently fabricating an image. */
function drawImagePlaceholder(slide: PowerPoint.Slide, shape: SlideShape, rect: RectPts, texts: StyledText[]): PowerPoint.Shape {
  const box = slide.shapes.addGeometricShape("Rectangle", {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  });
  box.name = SHAPE_PREFIX + shape.id;
  box.fill.setSolidColor(IMAGE_PLACEHOLDER_FILL);
  box.lineFormat.color = IMAGE_PLACEHOLDER_LINE;
  box.lineFormat.weight = 1;
  box.lineFormat.dashStyle = "Dash";

  const caption = slide.shapes.addTextBox(shape.text ?? IMAGE_PLACEHOLDER_CAPTION, {
    left: rect.left + 4,
    top: rect.top + rect.height / 2 - 10,
    width: Math.max(1, rect.width - 8),
    height: 20,
  });
  caption.name = TEXT_PREFIX + shape.id;
  texts.push({
    box: caption,
    size: IMAGE_CAPTION_FONT_SIZE,
    bold: false,
    color: IMAGE_PLACEHOLDER_LINE,
    align: PowerPoint.ParagraphHorizontalAlignment.center,
    italic: !shape.text,
    contextId: shape.id,
  });
  return box;
}

function drawShape(
  slide: PowerPoint.Slide,
  shape: SlideShape,
  slideWidthPts: number,
  slideHeightPts: number,
  themeColors: ThemeColors | null | undefined,
  texts: StyledText[],
): RectPts {
  const rect = toRectPts(shape, slideWidthPts, slideHeightPts);

  if (shape.role === "image") {
    drawImagePlaceholder(slide, shape, rect, texts);
    return rect;
  }

  const geometricType = resolveGeometricShapeType(shape.shapeType);
  if (geometricType) {
    drawContainerShape(slide, shape, rect, geometricType, themeColors, texts);
  } else if (shape.role === "container") {
    drawContainerShape(slide, shape, rect, "Rectangle", themeColors, texts);
  } else {
    drawTextOnlyShape(slide, shape, rect, themeColors, texts);
  }

  return rect;
}

/* ── Text styling (phased load/sync, matches orgChartBuilder.ts's styleTexts) ── */

async function styleTexts(context: PowerPoint.RequestContext, texts: readonly StyledText[]): Promise<void> {
  if (texts.length === 0) return;

  for (const t of texts) {
    try {
      t.box.fill.clear();
      t.box.lineFormat.visible = false;
    } catch (error) {
      // Best-effort per text box — a failure here leaves that one box with its
      // default fill/border but the run continues for every other box.
      logRenderFailure("sketch-to-slide-render-fill-clear", withContext(t.contextId, error));
    }
  }
  try {
    await context.sync();
  } catch (error) {
    // The whole batch failed to commit — every box in it is affected, not just one.
    logRenderFailure("sketch-to-slide-render-fill-clear-sync", error);
  }

  for (const t of texts) {
    t.box.textFrame.load("textRange,verticalAlignment");
  }
  try {
    await context.sync();
  } catch (error) {
    // Without the loaded textRange the styling loop below is a no-op for every
    // box (each `t.box.textFrame.textRange` read would itself throw) — the
    // boxes still render, just with PowerPoint's default text formatting.
    logRenderFailure("sketch-to-slide-render-text-load-sync", error);
    return;
  }

  for (const t of texts) {
    try {
      const range = t.box.textFrame.textRange;
      range.font.size = t.size;
      range.font.bold = t.bold;
      range.font.color = `#${t.color}`;
      if (t.italic) range.font.italic = true;
      range.paragraphFormat.horizontalAlignment = t.align;
      t.box.textFrame.verticalAlignment = PowerPoint.TextVerticalAlignment.middle;
    } catch (error) {
      logRenderFailure("sketch-to-slide-render-font-style", withContext(t.contextId, error));
    }
  }
  try {
    await context.sync();
  } catch (error) {
    logRenderFailure("sketch-to-slide-render-font-style-sync", error);
  }
}

/* ── Connectors ──────────────────────────────────────────────────────────── */

/** Plain string literals for the same reason `GEOMETRIC_SHAPE_TYPE_BY_PRST`
 *  above uses them, not `PowerPoint.ConnectorType.*` — this table is built at
 *  module load time. `addLine`'s string-literal overload accepts these directly. */
type ConnectorTypePreset = Parameters<PowerPoint.ShapeCollection["addLine"]>[0];

const CONNECTOR_TYPE_BY_STYLE: Record<SlideConnectorStyle, ConnectorTypePreset> = {
  straight: "Straight",
  elbow: "Elbow",
  curved: "Curve",
};

/** Edge-relative connection points between two shape rects — the connector
 *  leaves/enters whichever pair of edges is nearer along the dominant axis,
 *  rather than always drawing center-to-center through both shapes. */
function connectorEndpoints(from: RectPts, to: RectPts): { x1: number; y1: number; x2: number; y2: number } {
  const dx = to.cx - from.cx;
  const dy = to.cy - from.cy;

  if (Math.abs(dx) > Math.abs(dy)) {
    const x1 = dx > 0 ? from.left + from.width + CONNECTOR_GAP_PTS : from.left - CONNECTOR_GAP_PTS;
    const x2 = dx > 0 ? to.left - CONNECTOR_GAP_PTS : to.left + to.width + CONNECTOR_GAP_PTS;
    return { x1, y1: from.cy, x2, y2: to.cy };
  }
  const y1 = dy > 0 ? from.top + from.height + CONNECTOR_GAP_PTS : from.top - CONNECTOR_GAP_PTS;
  const y2 = dy > 0 ? to.top - CONNECTOR_GAP_PTS : to.top + to.height + CONNECTOR_GAP_PTS;
  return { x1: from.cx, y1, x2: to.cx, y2 };
}

/** Draws one connector; returns false (without throwing) for anything that
 *  makes it undrawable — a dangling/self-referencing endpoint or an Office.js
 *  failure — so one bad connector never aborts the rest of the render. */
function drawConnector(
  slide: PowerPoint.Slide,
  connector: SlideConnector,
  rectsById: ReadonlyMap<string, RectPts>,
  texts: StyledText[],
): boolean {
  const fromRect = rectsById.get(connector.fromId);
  const toRect = rectsById.get(connector.toId);
  if (!fromRect || !toRect) return false;
  // A shape connecting to itself has no meaningful edge-to-edge geometry
  // (connectorEndpoints would collapse to a near-zero-length line) — almost
  // certainly a model error, so it's treated the same as a dangling reference.
  if (connector.fromId === connector.toId) return false;

  try {
    const { x1, y1, x2, y2 } = connectorEndpoints(fromRect, toRect);
    const line = slide.shapes.addLine(CONNECTOR_TYPE_BY_STYLE[connector.style], {
      left: Math.min(x1, x2),
      top: Math.min(y1, y2),
      width: Math.max(1, Math.abs(x2 - x1)),
      height: Math.max(1, Math.abs(y2 - y1)),
    });
    line.name = CONNECTOR_PREFIX + connector.id;
    line.lineFormat.color = CONNECTOR_LINE_COLOR;
    line.lineFormat.weight = CONNECTOR_LINE_WEIGHT;

    if (connector.label) {
      const labelW = Math.max(50, connector.label.length * 6);
      const labelH = 18;
      const label = slide.shapes.addTextBox(connector.label, {
        left: (x1 + x2) / 2 - labelW / 2,
        top: (y1 + y2) / 2 - labelH / 2,
        width: labelW,
        height: labelH,
      });
      label.name = CONNECTOR_LABEL_PREFIX + connector.id;
      texts.push({
        box: label,
        size: CONNECTOR_LABEL_FONT_SIZE,
        bold: false,
        color: DEFAULT_TEXT_COLOR,
        align: PowerPoint.ParagraphHorizontalAlignment.center,
        contextId: connector.id,
      });
    }
    return true;
  } catch (error) {
    logRenderFailure("sketch-to-slide-render-connector-draw", withContext(connector.id, error));
    return false;
  }
}

/* ── Public API ──────────────────────────────────────────────────────────── */

export interface RenderSlideSpecResult {
  readonly slideId: string;
  readonly shapeCount: number;
  readonly connectorCount: number;
  readonly skippedShapeCount: number;
  readonly skippedConnectorCount: number;
}

/** Draw every shape, skipping (and logging) any that throws instead of
 *  aborting the whole render — one bad shape shouldn't cost the rest. A
 *  duplicate shape id is treated the same way: the backend's SlideSpec
 *  validator already rejects these, but resolving it silently in the
 *  frontend too (first one wins) is cheap defense-in-depth against a future
 *  contract drift, and prevents a connector from being wired to the wrong
 *  shape's geometry. */
function drawShapes(
  slide: PowerPoint.Slide,
  shapes: readonly SlideShape[],
  slideWidthPts: number,
  slideHeightPts: number,
  themeColors: ThemeColors | null | undefined,
  texts: StyledText[],
): { rectsById: Map<string, RectPts>; skippedShapeCount: number } {
  const rectsById = new Map<string, RectPts>();
  let skippedShapeCount = 0;
  for (const shape of shapes) {
    if (rectsById.has(shape.id)) {
      logRenderFailure("sketch-to-slide-render-duplicate-shape-id", new Error(`Duplicate shape id: ${shape.id}`));
      skippedShapeCount++;
      continue;
    }
    try {
      rectsById.set(shape.id, drawShape(slide, shape, slideWidthPts, slideHeightPts, themeColors, texts));
    } catch (error) {
      logRenderFailure("sketch-to-slide-render-shape-draw", withContext(shape.id, error));
      skippedShapeCount++;
    }
  }
  return { rectsById, skippedShapeCount };
}

function drawConnectors(
  slide: PowerPoint.Slide,
  connectors: readonly SlideConnector[],
  rectsById: ReadonlyMap<string, RectPts>,
  texts: StyledText[],
): { connectorCount: number; skippedConnectorCount: number } {
  let connectorCount = 0;
  let skippedConnectorCount = 0;
  for (const connector of connectors) {
    if (drawConnector(slide, connector, rectsById, texts)) connectorCount++;
    else skippedConnectorCount++;
  }
  if (skippedConnectorCount > 0) {
    logRenderFailure(
      "sketch-to-slide-render-connector-skipped",
      new Error(`${skippedConnectorCount} connector(s) could not be drawn (dangling/self-referencing endpoint, or a draw failure already logged individually)`),
    );
  }
  return { connectorCount, skippedConnectorCount };
}

function drawTitle(slide: PowerPoint.Slide, title: string, slideWidthPts: number): PowerPoint.Shape {
  const titleBox = slide.shapes.addTextBox(title, {
    left: 24,
    top: 8,
    width: Math.max(1, slideWidthPts - 48),
    height: 32,
  });
  titleBox.name = TITLE_PREFIX + "main";
  return titleBox;
}

async function styleTitle(context: PowerPoint.RequestContext, titleBox: PowerPoint.Shape): Promise<void> {
  try {
    titleBox.textFrame.load("textRange");
    await context.sync();
    titleBox.textFrame.textRange.font.size = TITLE_FONT_SIZE;
    titleBox.textFrame.textRange.font.bold = true;
    titleBox.textFrame.textRange.font.color = `#${DEFAULT_TEXT_COLOR}`;
    titleBox.fill.clear();
    titleBox.lineFormat.visible = false;
    await context.sync();
  } catch (error) {
    // The title text itself is already on the slide (addTextBox above doesn't
    // need a sync to take effect on the next one) — only its styling is at
    // risk here, so this degrades to an unstyled title box rather than failing
    // the whole render.
    logRenderFailure("sketch-to-slide-render-title-style", error);
  }
}

/**
 * Draw `slideSpec` as native shapes on a new slide appended to the end of the
 * deck, styled against `themeColors` where the spec supplies resolvable style
 * tokens (falling back to role-based defaults otherwise).
 *
 * @throws if PowerPoint is unavailable, the spec has no shapes to draw, or
 *   every shape in it fails to draw (nothing usable was produced).
 */
export async function renderSlideSpec(
  slideSpec: SlideSpec,
  themeColors: ThemeColors | null | undefined,
): Promise<RenderSlideSpecResult> {
  if (!hasPpt()) throw new Error(ERR_NO_PPT);
  if (slideSpec.shapes.length === 0) throw new Error(ERR_EMPTY_SPEC);

  let result: RenderSlideSpecResult = {
    slideId: "",
    shapeCount: 0,
    connectorCount: 0,
    skippedShapeCount: 0,
    skippedConnectorCount: 0,
  };

  await ppt().run(async (context) => {
    const { widthPts, heightPts } = await readSlideSizePts(context);
    const slide = await addNewSlide(context);

    try {
      const texts: StyledText[] = [];
      const { rectsById, skippedShapeCount } = drawShapes(slide, slideSpec.shapes, widthPts, heightPts, themeColors, texts);
      if (rectsById.size === 0) throw new Error(ERR_EMPTY_SPEC);
      await context.sync();

      const { connectorCount, skippedConnectorCount } = drawConnectors(slide, slideSpec.connectors, rectsById, texts);
      await context.sync();

      await styleTexts(context, texts);

      if (slideSpec.title) {
        await styleTitle(context, drawTitle(slide, slideSpec.title, widthPts));
      }

      result = {
        slideId: slide.id,
        shapeCount: rectsById.size,
        connectorCount,
        skippedShapeCount,
        skippedConnectorCount,
      };
    } catch (error) {
      await cleanupFailedSlide(context, slide, error);
      throw error;
    }
  });

  return result;
}
